///
/// EarthBrowser
///
/// Created by Matt Giger on 11/12/2023
/// Copyright (c) 2023 Geodata Labs
///

import { type matrix } from "./matrix"
import { vec3 } from "./vector"

/**
 * Represents a ray in 3D space.
 */
export class ray {

	public origin: vec3
	public direction: vec3
	public inverseDirection: vec3

	/**
	 * Creates a new ray instance.
	 * @param origin - The origin point of the ray (default: vec3.zero)
	 * @param direction - The direction vector of the ray (default: vec3.unitZ)
	 */
	constructor(origin: vec3 = vec3.zero, direction: vec3 = vec3.unitZ) {
		this.origin = origin
		this.direction = direction
		this.inverseDirection = new vec3(1 / direction.x, 1 / direction.y, 1 / direction.z)
	}

	/**
	 * Multiplies the ray by a matrix.
	 * @param m - The matrix to multiply by
	 * @returns A new ray resulting from the multiplication
	 */
	public multMatrix(m: matrix): ray {
		return new ray(
			m.multVec3(this.origin, 1),
			m.multVec3(this.direction, 0))
	}

	/**
	 * Calculates a point along the ray at a given distance.
	 * @param dist - The distance along the ray
	 * @returns The point at the specified distance
	 */
	public pointAt(dist: number): vec3 {
		return this.origin.add(this.direction.multiply(dist))
	}

	/**
	 * Returns a string representation of the ray.
	 * @returns A string in the format "origin -> direction"
	 */
	public toString(): string {
		return `${this.origin.toString()} -> ${this.direction.toString()}`
	}
}